import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import ParsonsCodingBuilderNodeView from './parsons-coding-builder-node-view';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        parsonsCodingBuilder: {
            insertParsonsCodingBuilder: (options?: InsertParsonsCodingBuilderOptions) => ReturnType;
        };
    }
}

export interface InsertParsonsCodingBuilderOptions {
    id?: string;
    caption?: string;
    parsonsCodingXMLToLoad?: string;
    options?: { [key: string]: any };
    challenge?: boolean;
}

export const ParsonsCodingBuilderNodeHeadless = Node.create({
    name: 'parsonsCodingBuilder',

    group: 'block',

    atom: true,

    selectable: true,

    draggable: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-parsons-coding-builder-id'),
                renderHTML: attributes => ({
                    'data-parsons-coding-builder-id': attributes.id,
                }),
            },
            caption: {
                default: '',
                parseHTML: element => element.getAttribute('data-caption') || '',
                renderHTML: attributes => ({
                    'data-caption': attributes.caption,
                }),
            },
            parsonsCodingXMLToLoad: {
                default: '',
                parseHTML: element => element.getAttribute('data-parsons-coding-xml') || '',
                renderHTML: attributes => ({
                    'data-parsons-coding-xml': attributes.parsonsCodingXMLToLoad,
                }),
            },
            options: {
                default: {},
                parseHTML: element => {
                    const raw = element.getAttribute('data-options');
                    try {
                        return raw ? JSON.parse(raw) : {};
                    } catch (error) {
                        console.error('Failed to parse data-options:', error, raw);
                        return {};
                    }
                },
                renderHTML: attributes => ({
                    'data-options': JSON.stringify(attributes.options),
                }),
            },
            challenge: {
                default: false,
                parseHTML: element => element.getAttribute('data-challenge') === 'true',
                renderHTML: attributes => ({
                    'data-challenge': attributes.challenge ? 'true' : 'false',
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="parsons-coding-builder"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'parsons-coding-builder',
                class: 'parsons-coding-builder-container',
            }),
        ];
    },

    addCommands() {
        return {
            insertParsonsCodingBuilder:
                (options: InsertParsonsCodingBuilderOptions = {}) =>
                ({ commands }) => {
                    const emptyParsonsCodingXML = `<zyTool name="ParsonsCodingPA" caption="" parts="1" challenge="false" edited-last="Mon Feb 02 2026 14:45:41 GMT-0800 (Pacific Standard Time)"><zyOptions type="dict"><files type="list"><li type="dict"><contents type="list"><li type="dict"><distractors type="list"></distractors><editable type="boolean">false</editable><indents type="number">0</indents><solution type="string">#include &lt;stdio.h&gt;

int main(void) {</solution></li><li type="dict"><distractors type="list"></distractors><editable type="boolean">true</editable><indents type="number">1</indents><solution type="string"></solution></li></contents><input type="boolean">false</input><main type="boolean">true</main><name type="string">main.c</name></li></files><language type="string">c</language><languageOption type="string">Classic C</languageOption><prompt type="string"></prompt><tests type="list"><li type="dict"><filename type="string"></filename><stdin type="string"></stdin><type type="string">stdout</type></li></tests></zyOptions></zyTool>`;

                    return commands.insertContent({
                        type: this.name,
                        attrs: {
                            caption: options.caption || '',
                            parsonsCodingXMLToLoad: options.parsonsCodingXMLToLoad || emptyParsonsCodingXML,
                            options: options.options || {},
                            challenge: options.challenge || false,
                        },
                    });
                },
        };
    },
});

export const ParsonsCodingBuilderNode = ParsonsCodingBuilderNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ParsonsCodingBuilderNodeView);
    },
});

export default ParsonsCodingBuilderNode;
